import React, { useEffect, useMemo, useState } from "react";
import { Dialog, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2 } from "lucide-react";
import { apiFetch } from "@/lib/api";
import { toastError, toastSuccess } from "@/lib/toast";
import { ResponsiveDialogContent } from "@/components/ResponsiveDialogContent";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";
import { DatePickerField } from "@/components/DatePickerField";

type LoanLite = {
  id: number;
  loan_number?: string | number;
  fname?: string;
  sname?: string;
  phone_number?: string;
  BRANCH?: string;
  PRODUCT?: string;
  co_status?: number | string | null;
};

const STATUS_OPTIONS = [
  { value: "3", label: "Approved" },
  { value: "4", label: "Rejected" },
];

export function LoanApprovalDialog({
  open,
  onOpenChange,
  loan,
  endpoint, // (loanId) => string
  onSuccess,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  loan: LoanLite | null;
  endpoint: (loanId: string) => string;
  onSuccess?: () => void;
}) {
  const initial = useMemo(() => {
    return {
      status: String(loan?.co_status ?? "1"),
      more_info: "",
      date: new Date().toISOString().slice(0, 10), // MySQL format
    };
  }, [loan]);

  const [form, setForm] = useState(initial);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) setForm(initial);
  }, [open, initial]);

  const canSubmit = useMemo(() => {
    return form.status === "0" || form.status === "1";
  }, [form.status]);

  async function submit() {
    if (!loan?.id) {
      toastError("No loan selected");
      return;
    }
    if (!canSubmit || saving) return;

    setSaving(true);
    try {
      const res = await apiFetch<any>(endpoint(String(loan.id)), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          status: Number(form.status),
          more_info: form.more_info,
        }),
      });

      if (res?.ok === false) {
        toastError(res?.message ?? "Failed");
        return;
      }

      toastSuccess(res?.message ?? "Loan approval saved");
      onOpenChange(false);
      onSuccess?.();
    } catch (e: any) {
      toastError(e?.message ?? "Failed");
    } finally {
      setSaving(false);
    }
  }

  const titleLine = loan
    ? `Loan #${loan.loan_number ?? loan.id} • ${loan.fname ?? ""} ${loan.sname ?? ""}`.trim()
    : "Branch Approve Loan";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <ResponsiveDialogContent>
        <div className="border-b border-border bg-muted/30 px-5 py-4">
          <DialogHeader>
            <DialogTitle className="text-xl">Branch Loan Approval</DialogTitle>
            <div className="text-sm text-muted-foreground mt-1">{titleLine}</div>
          </DialogHeader>
        </div>

        <div className="px-5 py-4 space-y-4">
          {/* Status */}
          <div>
            <Label className="text-sm font-medium">Approval Date <span className="text-destructive">*</span></Label>
            <div className="mt-2">

         <DatePickerField value={form.date} onChange={(v) => setForm((p) => ({ ...p, date: v }))} placeholder="Approval date" />
                  
            </div>
            </div>
          <div>
            <Label className="text-sm font-medium">Status <span className="text-destructive">*</span></Label>
            <div className="mt-2">
              <Select
                value={form.status}
                onValueChange={(v) => setForm((p) => ({ ...p, status: v }))}
                disabled={saving}
              >
                <SelectTrigger className={cn(saving && "opacity-70")}>
                  <SelectValue placeholder="Select" />
                </SelectTrigger>
                <SelectContent>
                  {STATUS_OPTIONS.map((o) => (
                    <SelectItem key={o.value} value={o.value}>
                      {o.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Remarks */}
          <div>
            <Label className="text-sm font-medium">Comment</Label>
            <Textarea
              className={cn("mt-2 min-h-[120px]", saving && "opacity-70")}
              placeholder="Optional remarks..."
              value={form.more_info}
              onChange={(e) => setForm((p) => ({ ...p, more_info: e.target.value }))}
              disabled={saving}
            />
          </div>
        </div>

        <div className="border-t border-border px-5 py-4">
          <DialogFooter className="gap-2 sm:gap-3">
            <Button variant="outline" type="button" onClick={() => onOpenChange(false)} disabled={saving}>
              Close
            </Button>
            <Button type="button" onClick={submit} disabled={!canSubmit || saving}>
              {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
              Process
            </Button>
          </DialogFooter>
        </div>
      </ResponsiveDialogContent>
    </Dialog>
  );
}